import React, { useEffect, useState } from "react";
import { Button, Row, Col, ListGroup, Badge, Modal } from "react-bootstrap";
import Fade from "react-reveal/Slide";
import healthRevolution from "../assets/predictiveHealth.png";
import aiTriad from "../assets/aiTriad.png";
import medicalIntelligence from "../assets/medicalIntelligence.png";
import familyGenetics from "../assets/familyGenetics.png";
import environmentalAI from "../assets/environmentalAI.png";
import anonMentalHealth from "../assets/anonMentalHealth.png";
import predictiveHealth from "../assets/predictiveHealth.png";
import indianHealthAI from "../assets/indianHealthAI.png";
import tik from "../assets/tik.png";

export default function RichHealth() {
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: "", content: "" });

  useEffect(() => {
    document.body.classList.add("richhealth-page");
    return () => {
      document.body.classList.remove("richhealth-page");
    };
  }, []);

  const openModal = (type) => {
    let content = {};
    switch (type) {
      case "privacy":
        content = {
          title: "Privacy Policy",
          content:
            "Your health data privacy is our top priority. RichHealth implements end-to-end encryption, anonymous processing, and strict data governance to ensure your personal health information remains secure and private. We never sell your data and provide complete transparency about how your information is used to improve your health outcomes.",
        };
        break;
      case "terms":
        content = {
          title: "Terms and Conditions",
          content:
            "By using RichHealth, you agree to our comprehensive terms of service. Our platform provides AI-powered health insights for informational purposes and should not replace professional medical advice. Users retain full ownership of their health data and can export or delete their information at any time.",
        };
        break;
      case "refund":
        content = {
          title: "Refund/Cancellation Policy",
          content:
            "RichHealth offers flexible subscription management with full refunds within 3 days of purchase. You can cancel your subscription at any time, and your access will continue until the end of your billing period. Pro and Premium subscribers receive prorated refunds for unused portions of their subscription.",
        };
        break;
      default:
        content = { title: "", content: "" };
    }
    setModalContent(content);
    setShowModal(true);
  };

  return (
    <div className="faqBody richHealthContainer">
      {/* Hero Section with Proper Branding */}
      <div className="cardStyles fullWidthHomeCard richHealthHero">
        <Row>
          <Col sm={8}>
            <Fade left>
              <div className="richHealthHeroContent">
                <div className="richHealthBrandSection">
                  <h1 className="richHealthBrandName">RichHealth</h1>
                  <p className="richHealthBrandTagline">
                    Where Intelligence Meets Health. Where Health Meets Life.
                  </p>
                </div>
                <p className="richHealthHeroSubtitle">
                  The world's first holistic health intelligence platform
                  powered by a trinity of AI minds. AI that reads Indian medical
                  reports as fluently as Western ones, provides anonymous mental
                  health support, and predicts your health future by
                  understanding your family's past.
                </p>
                <div className="richHealthHeroStats">
                  <div className="richHealthStat">
                    <span className="richHealthStatNumber">1.4B+</span>
                    <span className="richHealthStatLabel">
                      Lives We're Transforming
                    </span>
                  </div>
                  <div className="richHealthStat">
                    <span className="richHealthStatNumber">3 AI</span>
                    <span className="richHealthStatLabel">
                      Minds Working as One
                    </span>
                  </div>
                  <div className="richHealthStat">
                    <span className="richHealthStatNumber">∞</span>
                    <span className="richHealthStatLabel">
                      Privacy Protection
                    </span>
                  </div>
                </div>
                <Button className="richHealthCtaButton">
                  Experience RichHealth Now
                </Button>
              </div>
            </Fade>
          </Col>
          <Col sm={4}>
            <Fade right>
              <div className="richHealthHeroImage">
                <img
                  src={healthRevolution}
                  alt="RichHealth Platform"
                  className="richHealthMainImage"
                />
              </div>
            </Fade>
          </Col>
        </Row>
      </div>

      {/* Core Features - Moved Up */}
      <div className="richHealthFeaturesGrid">
        <Fade bottom>
          <h1 className="richHealthSectionTitle">
            Revolutionary Features That Transform Lives
          </h1>
        </Fade>

        <div className="richHealthGridContainer">
          <Fade bottom>
            <div className="richHealthFeatureCard">
              <div className="richHealthFeatureIcon">
                <img src={anonMentalHealth} alt="Anonymous Mental Health" />
              </div>
              <h3>Anonymous Mental Health</h3>
              <p className="richHealthFeatureTagline">
                Privacy as a Fundamental Right
              </p>
              <ul className="richHealthFeatureList">
                <li>No sign-ups, no history, no digital footprints</li>
                <li>
                  Culturally sensitive Indian family dynamics understanding
                </li>
                <li>Engineered invisibility - speak without fear</li>
                <li>Real-time support without stigma</li>
              </ul>
              <div className="richHealthImpactBadge">60% Stress Reduction</div>
            </div>
          </Fade>

          <Fade bottom>
            <div className="richHealthFeatureCard">
              <div className="richHealthFeatureIcon">
                <img src={environmentalAI} alt="Environmental Intelligence" />
              </div>
              <h3>Environmental Intelligence</h3>
              <p className="richHealthFeatureTagline">Your Health in Context</p>
              <ul className="richHealthFeatureList">
                <li>Personalized AQI alerts based on your conditions</li>
                <li>Smart API cost reduction by 70%</li>
                <li>Pollution-health correlation tracking</li>
                <li>Preventive intervention recommendations</li>
              </ul>
              <div className="richHealthImpactBadge">
                60% Fewer Asthma Attacks
              </div>
            </div>
          </Fade>

          <Fade bottom>
            <div className="richHealthFeatureCard">
              <div className="richHealthFeatureIcon">
                <img src={familyGenetics} alt="Family Health Networks" />
              </div>
              <h3>Family Health Networks</h3>
              <p className="richHealthFeatureTagline">
                Your Genetic Crystal Ball
              </p>
              <ul className="richHealthFeatureList">
                <li>Multi-generational pattern recognition</li>
                <li>Predictive genetic risk assessment</li>
                <li>Real-time family health alerts</li>
                <li>Privacy-controlled data sharing</li>
              </ul>
              <div className="richHealthImpactBadge">
                Early Disease Prevention
              </div>
            </div>
          </Fade>

          <Fade bottom>
            <div className="richHealthFeatureCard">
              <div className="richHealthFeatureIcon">
                <img src={medicalIntelligence} alt="Document Intelligence" />
              </div>
              <h3>Medical Document Intelligence</h3>
              <p className="richHealthFeatureTagline">
                From Paper Chaos to Digital Clarity
              </p>
              <ul className="richHealthFeatureList">
                <li>5-year health story visualization</li>
                <li>Pattern recognition across years of reports</li>
                <li>Doctor verification layer</li>
                <li>Predictive health insights</li>
              </ul>
              <div className="richHealthImpactBadge">Early Detection</div>
            </div>
          </Fade>
        </div>
      </div>

      {/* AI Trinity Section */}
      <div className="richHealthSection">
        <Fade bottom>
          <div className="richHealthSectionHeader">
            <h1 className="richHealthSectionTitle">
              The Trinity of Medical AI
            </h1>
            <p className="richHealthSectionSubtitle">Three Minds, One Vision</p>
          </div>
        </Fade>

        <Row style={{ marginTop: 50 }}>
          <Col sm={6}>
            <Fade left>
              <div className="richHealthAITriadImage">
                <img
                  src={aiTriad}
                  alt="AI Trinity"
                  className="richHealthFeatureImage"
                />
              </div>
            </Fade>
          </Col>
          <Col sm={6}>
            <Fade right>
              <div className="richHealthAITriadContent">
                <div className="richHealthAICard">
                  <h4>Google Gemini: Your Health Companion</h4>
                  <p>
                    Remembers, understands, and connects. Correlates your
                    evening fatigue with last month's low hemoglobin report.
                  </p>
                </div>
                <div className="richHealthAICard">
                  <h4>ChatGPT: The Medical Document Virtuoso</h4>
                  <p>
                    Reads between the lines of complex reports, understanding
                    that borderline values spell trouble when combined.
                  </p>
                </div>
                <div className="richHealthAICard">
                  <h4>Proprietary Indian Medical LLM</h4>
                  <p>
                    The first AI that truly speaks Indian healthcare. Knows
                    "Crocin" means paracetamol and "sugar" means diabetes.
                  </p>
                </div>
                <div className="richHealthSynthesis">
                  <h4 className="richHealthSynthesisTitle">
                    The Synthesis: Where Magic Happens
                  </h4>
                  <p>
                    These three AIs symphony together, finding patterns no human
                    doctor could spot across thousands of data points.
                  </p>
                </div>
              </div>
            </Fade>
          </Col>
        </Row>
      </div>

      {/* The Question That Changed Everything */}
      <Fade bottom>
        <div className="richHealthQuestionSection">
          <h2 className="richHealthQuestionTitle">
            The Question That Changed Everything
          </h2>
          <div className="richHealthQuestionBox">
            <p className="richHealthQuestion">
              "In a world where a smartwatch counts your steps but can't read
              your medical reports, where mental health apps breach your
              privacy, where your family's medical history sits in dusty folders
              while AI remains untrained on your population's health data—"
            </p>
            <h3 className="richHealthQuestionAnswer">
              What if AI understood your health better than you do?
            </h3>
          </div>
        </div>
      </Fade>

      {/* Pricing - Simplified */}
      <div className="richHealthBusinessSection">
        <Fade bottom>
          <h1 className="richHealthSectionTitle">Choose Your Health Journey</h1>
        </Fade>

        <div className="richHealthBusinessGrid">
          <Fade left>
            <div className="richHealthBusinessCard">
              <h3>Personal Plans</h3>
              <div className="richHealthPricingTiers">
                <div className="richHealthTier">
                  <span className="richHealthTierName">Free</span>
                  <span className="richHealthTierPrice">₹0</span>
                  <span className="richHealthTierFeatures">
                    Basic tracking, limited AI
                  </span>
                </div>
                <div className="richHealthTier richHealthTierPopular">
                  <span className="richHealthTierName">Pro</span>
                  <span className="richHealthTierPrice">₹299/mo</span>
                  <span className="richHealthTierFeatures">
                    Unlimited AI, family sharing
                  </span>
                </div>
                <div className="richHealthTier">
                  <span className="richHealthTierName">Premium</span>
                  <span className="richHealthTierPrice">₹999/mo</span>
                  <span className="richHealthTierFeatures">
                    Doctor consultations, predictions
                  </span>
                </div>
              </div>
            </div>
          </Fade>

          <Fade right>
            <div className="richHealthBusinessCard">
              <h3>Enterprise Solutions</h3>
              <ul className="richHealthBusinessList">
                <li>Corporate Wellness Programs</li>
                <li>Insurance Risk Reduction</li>
                <li>Hospital Patient Monitoring</li>
                <li>Population Health Analytics</li>
              </ul>
              <Button className="richHealthEnterpriseButton">
                Contact Sales
              </Button>
            </div>
          </Fade>
        </div>
      </div>

      {/* Future Roadmap - Condensed */}
      <div className="richHealthRoadmapSection">
        <Fade bottom>
          <h1 className="richHealthSectionTitle">The Future We're Building</h1>
        </Fade>

        <div className="richHealthRoadmap">
          <div className="richHealthRoadmapItem richHealthToday">
            <Fade left>
              <div className="richHealthRoadmapContent">
                <h3>Today: Comprehensive Understanding</h3>
                <p>
                  RichHealth aggregates and analyzes health data more thoroughly
                  than humanly possible
                </p>
                <div className="richHealthRoadmapBadge">LIVE NOW</div>
              </div>
            </Fade>
          </div>

          <div className="richHealthRoadmapItem richHealthTomorrow">
            <Fade bottom>
              <div className="richHealthRoadmapContent">
                <h3>Coming Soon: Predictive Prevention</h3>
                <p>
                  AI predicts health issues weeks before symptoms, providing
                  personalized reversal protocols
                </p>
                <div className="richHealthRoadmapBadge">IN DEVELOPMENT</div>
              </div>
            </Fade>
          </div>

          <div className="richHealthRoadmapItem richHealthFuture">
            <Fade right>
              <div className="richHealthRoadmapContent">
                <h3>Future: Autonomous Optimization</h3>
                <p>
                  Connected with IoT devices, continuously optimizes your health
                  in real-time
                </p>
                <div className="richHealthRoadmapBadge">COMING SOON</div>
              </div>
            </Fade>
          </div>
        </div>
      </div>

      {/* Download & Get Started */}
      <div className="richHealthPromiseSection">
        <Fade bottom>
          <div className="richHealthPromise">
            <h2>Ready to Transform Your Health?</h2>
            <p>
              Join thousands who trust RichHealth for comprehensive health
              management. Experience the perfect blend of advanced technology
              and elegant design.
            </p>

            <div className="richHealthDownloadSection">
              <h3>Get RichHealth Today</h3>
              <p className="richHealthDownloadText">
                Download now and start your journey to optimal health with
                AI-powered intelligence.
              </p>
              <div className="richHealthDownloadButtons">
                <a
                  href="https://play.google.com/store"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="richHealthDownloadLink"
                >
                  <img
                    src="https://storage.googleapis.com/proudcity/montclairca/uploads/2021/03/Google-Play-Download-Link.png"
                    alt="Download RichHealth on Google Play"
                    className="richHealthPlayStoreButton"
                  />
                </a>
                <Button className="richHealthWebAppButton">
                  Try Web App Free
                </Button>
              </div>
              <div className="richHealthDownloadStats">
                <div className="richHealthDownloadStat">
                  <span className="richHealthDownloadNumber">4.8★</span>
                  <span className="richHealthDownloadLabel">User Rating</span>
                </div>
                <div className="richHealthDownloadStat">
                  <span className="richHealthDownloadNumber">100K+</span>
                  <span className="richHealthDownloadLabel">Active Users</span>
                </div>
                <div className="richHealthDownloadStat">
                  <span className="richHealthDownloadNumber">24/7</span>
                  <span className="richHealthDownloadLabel">AI Support</span>
                </div>
              </div>
            </div>

            <div className="richHealthDeveloper">
              <span>Developed by </span>
              <strong>RichLabs.io</strong>
              <span className="richHealthCopyright">
                {" "}
                • © 2024 RichHealth Technologies
              </span>
            </div>
          </div>
        </Fade>
      </div>

      {/* Legal Footer with Modal Triggers */}
      <div className="richHealthLegalSection">
        <Fade bottom>
          <div className="richHealthLegalCard">
            <h3 className="richHealthLegalTitle">Legal & Policies</h3>
            <div className="richHealthLegalGrid">
              <div className="richHealthLegalItem">
                <h4>Privacy Policy</h4>
                <p>
                  Your health data privacy is our top priority. Learn how we
                  protect your personal information.
                </p>
                <Button
                  className="richHealthLegalButton"
                  onClick={() => openModal("privacy")}
                >
                  View Privacy Policy
                </Button>
              </div>
              <div className="richHealthLegalItem">
                <h4>Terms and Conditions</h4>
                <p>
                  Understand your rights and responsibilities when using the
                  RichHealth platform.
                </p>
                <Button
                  className="richHealthLegalButton"
                  onClick={() => openModal("terms")}
                >
                  View Terms & Conditions
                </Button>
              </div>
              <div className="richHealthLegalItem">
                <h4>Refund/Cancellation Policy</h4>
                <p>
                  Flexible subscription management with transparent refund and
                  cancellation policies.
                </p>
                <Button
                  className="richHealthLegalButton"
                  onClick={() => openModal("refund")}
                >
                  View Refund Policy
                </Button>
              </div>
            </div>
          </div>
        </Fade>
      </div>

      {/* Modal for Legal Content */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{ backgroundColor: "#1A1A1A", borderBottom: "1px solid #333" }}
        >
          <Modal.Title style={{ color: "#008b8b" }}>
            {modalContent.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: "#1A1A1A", color: "#ffffff" }}>
          <p style={{ lineHeight: "1.6", fontSize: "16px" }}>
            {modalContent.content}
          </p>
        </Modal.Body>
        <Modal.Footer
          style={{ backgroundColor: "#1A1A1A", borderTop: "1px solid #333" }}
        >
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            style={{ backgroundColor: "#008b8b", borderColor: "#008b8b" }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
